// client/lib/fixorium-chart.ts

export interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export type TimeFrame = '1H' | '4H' | '1D' | '1W' | '1M' | '3M' | '4M';

// DexScreener API
const DEXSCREENER_API = 'https://api.dexscreener.com/latest/dex';

// Map timeframes to minutes
const TIMEFRAME_MINUTES: Record<TimeFrame, number> = {
  '1H': 60,
  '4H': 240,
  '1D': 1440,
  '1W': 10080,
  '1M': 43200,
  '3M': 129600,
  '4M': 172800,
};

// Map timeframes to number of candles
const TIMEFRAME_CANDLES: Record<TimeFrame, number> = {
  '1H': 24,
  '4H': 30,
  '1D': 90,
  '1W': 16,
  '1M': 4,
  '3M': 1,
  '4M': 1,
};

// Get chart data
export async function getFixoriumChartData(
  tokenAddress: string,
  timeframe: TimeFrame = '1D'
): Promise<CandleData[]> {
  try {
    // Get token data from DexScreener
    const dexUrl = `${DEXSCREENER_API}/tokens/${tokenAddress}`;
    const response = await fetch(dexUrl);
    const data = await response.json();
    
    if (data && data.pairs && data.pairs.length > 0) {
      const bestPair = data.pairs.sort((a: any, b: any) => 
        (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0)
      )[0];
      
      const currentPrice = parseFloat(bestPair.priceUsd);
      const priceChange24h = parseFloat(bestPair.priceChange?.h24 || '0');
      const volume24h = parseFloat(bestPair.volume?.h24 || '0');
      
      // Generate realistic candles
      const candles: CandleData[] = [];
      const now = Date.now();
      const dataPoints = TIMEFRAME_CANDLES[timeframe];
      const intervalMs = TIMEFRAME_MINUTES[timeframe] * 60 * 1000;
      
      for (let i = dataPoints; i >= 0; i--) {
        const candleTime = now - (i * intervalMs);
        const progress = i / dataPoints;
        const changeFactor = 1 + (priceChange24h / 100) * (1 - progress);
        const basePrice = currentPrice / changeFactor;
        
        // Add realistic volatility
        const volatility = 0.03;
        const open = basePrice * (1 + (Math.random() - 0.5) * volatility);
        const close = basePrice * (1 + (Math.random() - 0.5) * volatility);
        const high = Math.max(open, close) * (1 + Math.random() * volatility);
        const low = Math.min(open, close) * (1 - Math.random() * volatility);
        const volume = (volume24h / dataPoints) * (0.5 + Math.random());
        
        candles.push({
          time: Math.floor(candleTime / 1000),
          open,
          high,
          low,
          close,
          volume,
        });
      }
      
      return candles;
    }
    
    return [];
    
  } catch (error) {
    console.error('[Chart] Error fetching data:', error);
    return [];
  }
}

// Get current token data with real-time info
export async function getCurrentTokenData(tokenAddress: string) {
  try {
    const url = `${DEXSCREENER_API}/tokens/${tokenAddress}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data && data.pairs && data.pairs.length > 0) {
      const bestPair = data.pairs.sort((a: any, b: any) => 
        (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0)
      )[0];
      
      const currentPrice = parseFloat(bestPair.priceUsd);
      const priceChange24h = parseFloat(bestPair.priceChange?.h24 || '0');
      const volume24h = parseFloat(bestPair.volume?.h24 || '0');
      const liquidity = parseFloat(bestPair.liquidity?.usd || '0');
      
      const volatility = Math.abs(priceChange24h) / 100;
      const high24h = currentPrice * (1 + volatility);
      const low24h = currentPrice * (1 - volatility);
      
      return {
        price: currentPrice,
        priceChange24h: priceChange24h,
        volume24h: volume24h,
        liquidity: liquidity,
        high24h: high24h,
        low24h: low24h,
        pairAddress: bestPair.pairAddress,
        dex: bestPair.dexId,
        baseToken: bestPair.baseToken,
        quoteToken: bestPair.quoteToken,
        url: bestPair.url,
      };
    }
    
    return null;
  } catch (error) {
    console.error('[Chart] Error fetching current data:', error);
    return null;
  }
}

// Get real-time price only
export async function getRealTimePrice(tokenAddress: string): Promise<number> {
  try {
    const url = `${DEXSCREENER_API}/tokens/${tokenAddress}`;
    const response = await fetch(url);
    const data = await response.json();
    
    if (data && data.pairs && data.pairs.length > 0) {
      const bestPair = data.pairs.sort((a: any, b: any) => 
        (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0)
      )[0];
      return parseFloat(bestPair.priceUsd);
    }
    
    return 0;
  } catch (error) {
    console.error('[Chart] Error fetching real-time price:', error);
    return 0;
  }
}

// Get multiple timeframes data
export async function getAllTimeframesData(
  tokenAddress: string
): Promise<Record<TimeFrame, CandleData[]>> {
  const timeframes: TimeFrame[] = ['1H', '4H', '1D', '1W', '1M'];
  const results: Record<TimeFrame, CandleData[]> = {} as Record<TimeFrame, CandleData[]>;
  
  await Promise.all(
    timeframes.map(async (tf) => {
      results[tf] = await getFixoriumChartData(tokenAddress, tf);
    })
  );
  
  return results;
}
